import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
  Building2, Users, ListChecks, Plus, Loader2, KeyRound, UserX, UserCheck, LogOut, Car,
  FileText, Download, BarChart3, Wallet, Archive, RotateCcw, Check, X,
} from "lucide-react";
import { CorporateBookingModal } from "./admin/CorporateBookingModal";

const inputCls = "w-full border border-border px-3 py-2 text-sm focus:outline-none focus:border-[#002FA7]";
const gbp = (n) => `£${Number(n || 0).toFixed(2)}`;

export default function CorporatePortal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("team");
  const [users, setUsers] = useState(null);
  const [bookings, setBookings] = useState(null);
  const [adding, setAdding] = useState(false);
  const [newUser, setNewUser] = useState({});
  const [busy, setBusy] = useState(false);
  const [booking, setBooking] = useState(false);
  const [costCentres, setCostCentres] = useState([]);
  const [manageCC, setManageCC] = useState(null);
  const [ccBudgets, setCcBudgets] = useState({});
  const [editBudget, setEditBudget] = useState({});   // ccid -> string value being edited
  const [showArchivedCC, setShowArchivedCC] = useState(false);
  const [newCC, setNewCC] = useState({});
  const [addingCC, setAddingCC] = useState(false);

  const loadManageCC = useCallback(async (incArchived) => {
    try {
      const [list, budgets] = await Promise.all([
        api.get(`/corporate/me/cost-centres?include_archived=${incArchived ? "true" : "false"}`),
        api.get("/corporate/me/cost-centre-budgets"),
      ]);
      setManageCC(list.data.cost_centres || []);
      const map = {};
      (budgets.data.cost_centres || []).forEach((b) => { map[b.cost_centre_id] = b; });
      setCcBudgets(map);
    } catch (e) { toast.error(formatApiError(e) || "Failed to load cost centres"); }
  }, []);
  useEffect(() => { if (tab === "costcentres") loadManageCC(showArchivedCC); }, [tab, showArchivedCC, loadManageCC]);

  const createCC = async () => {
    if (!newCC.name) { toast.message("Cost centre name is required."); return; }
    try {
      await api.post("/corporate/me/cost-centres", {
        name: newCC.name, code: newCC.code || null,
        monthly_budget: newCC.monthly_budget ? Number(newCC.monthly_budget) : null,
      });
      toast.success("Cost centre created.");
      setNewCC({}); setAddingCC(false);
      await Promise.all([loadManageCC(showArchivedCC), load()]);
    } catch (e) { toast.error(formatApiError(e) || "Could not create cost centre"); }
  };

  const saveBudget = async (ccid) => {
    const raw = editBudget[ccid];
    const val = (raw ?? "").toString().trim();
    try {
      await api.put(`/corporate/me/cost-centres/${ccid}`,
        val === "" ? { clear_budget: true } : { monthly_budget: Number(val) });
      toast.success("Budget updated.");
      setEditBudget((m) => { const n = { ...m }; delete n[ccid]; return n; });
      await loadManageCC(showArchivedCC);
    } catch (e) { toast.error(formatApiError(e) || "Could not update budget"); }
  };

  const ccAction = async (ccid, action) => {
    try {
      await api.post(`/corporate/me/cost-centres/${ccid}/${action}`);
      toast.success(action === "archive" ? "Cost centre archived." : "Cost centre restored.");
      await Promise.all([loadManageCC(showArchivedCC), load()]);
    } catch (e) { toast.error(formatApiError(e) || "Action failed"); }
  };

  const setStaffDefaultCC = async (uid, ccid) => {
    try {
      await api.put(`/corporate/me/users/${uid}/default-cost-centre`, { default_cost_centre_id: ccid || null });
      toast.success("Default cost centre updated.");
      await load();
    } catch (e) { toast.error(formatApiError(e) || "Could not update default cost centre"); }
  };

  const createBooking = async (payload) => {
    try {
      await api.post("/corporate/me/bookings", payload);
      toast.success("Ride booked & dispatched.");
      setBookings(null); // force reload
      if (tab === "bookings") loadBookings();
    } catch (e) {
      toast.error(formatApiError(e) || "Could not book ride");
      throw e;
    }
  };

  const load = useCallback(async () => {
    try {
      const [m, u, cc] = await Promise.all([
        api.get("/corporate/me"),
        api.get("/corporate/me/users"),
        api.get("/corporate/me/cost-centres"),
      ]);
      setMe(m.data); setUsers(u.data.users || []); setCostCentres(cc.data.cost_centres || []);
    } catch (e) { toast.error(formatApiError(e) || "Failed to load"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const loadBookings = useCallback(async () => {
    try { const { data } = await api.get("/corporate/me/bookings"); setBookings(data.bookings || []); }
    catch (e) { toast.error(formatApiError(e) || "Failed to load bookings"); }
  }, []);
  useEffect(() => { if (tab === "bookings" && bookings === null) loadBookings(); }, [tab, bookings, loadBookings]);

  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [statement, setStatement] = useState(null);
  const [spend, setSpend] = useState(null);
  const [stmtLoading, setStmtLoading] = useState(false);

  const loadStatement = useCallback(async (m) => {
    setStmtLoading(true); setStatement(null); setSpend(null);
    try {
      const [s, sp] = await Promise.all([
        api.get(`/corporate/me/statement?month=${m}`),
        api.get(`/corporate/me/spend?month=${m}`),
      ]);
      setStatement(s.data); setSpend(sp.data);
    } catch (e) { toast.error(formatApiError(e) || "Failed to load statement"); }
    finally { setStmtLoading(false); }
  }, []);
  useEffect(() => { if (tab === "statements") loadStatement(month); }, [tab, month, loadStatement]);

  const downloadStatement = async (ext) => {
    try {
      const res = await api.get(`/corporate/me/statement.${ext}?month=${month}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `statement_${month}.${ext}`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e) || `Could not download ${ext.toUpperCase()}`); }
  };

  const addUser = async () => {
    if (!newUser.name || !newUser.email) { toast.message("Name and email are required."); return; }
    setBusy(true);
    try {
      await api.post("/corporate/me/users", { name: newUser.name, email: newUser.email, phone: newUser.phone || null, default_cost_centre_id: newUser.default_cost_centre_id || null });
      toast.success("Staff member added — a set-password email was sent.");
      setNewUser({}); setAdding(false); await load();
    } catch (e) { toast.error(formatApiError(e) || "Could not add user"); }
    finally { setBusy(false); }
  };

  const act = async (uid, action) => {
    try {
      const { data } = await api.post(`/corporate/me/users/${uid}/${action}`);
      if (action === "send-reset") toast.success("Password reset link sent.");
      else { toast.success(data.disabled ? "User disabled" : "User enabled"); await load(); }
    } catch (e) { toast.error(formatApiError(e) || "Action failed"); }
  };

  const company = me?.company;

  return (
    <div className="min-h-screen bg-[#FAFAF7]" data-testid="corporate-portal">
      <header className="bg-[#0A0A0A] text-white">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <Building2 className="w-5 h-5 text-[#FFC107]" />
            <div className="min-w-0">
              <div className="font-heading font-black text-lg truncate" data-testid="corporate-company-name">{company?.name || "Corporate portal"}</div>
              <div className="text-[11px] text-white/60 truncate">{user?.name} · {user?.email}</div>
            </div>
          </div>
          <button onClick={async () => { await logout?.(); navigate("/login"); }} className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-white/80 hover:text-[#FFC107]" data-testid="corporate-signout">
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-6">
        {company && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
            <Stat label="Company" value={company.name} testid="corp-stat-name" />
            <Stat label="Billing" value={company.payment_preference || "—"} testid="corp-stat-billing" />
            <Stat label="Status" value={company.status || "active"} testid="corp-stat-status" />
          </div>
        )}

        <div className="flex gap-px bg-border mb-4 w-full md:w-auto md:inline-flex" data-testid="corporate-tabs">
          {[["team", "Team", Users], ["costcentres", "Cost centres", Wallet], ["bookings", "Bookings", ListChecks], ["statements", "Statements", FileText]].map(([id, label, Icon]) => (
            <button key={id} onClick={() => setTab(id)} className={`flex items-center gap-1.5 px-4 py-2 text-sm font-bold ${tab === id ? "bg-[#0A0A0A] text-white" : "bg-white hover:bg-secondary"}`} data-testid={`corporate-tab-${id}`}>
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>

        {tab === "team" && (
          <div className="bg-white border border-border" data-testid="corporate-team">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <h2 className="font-heading font-black text-lg">Team members</h2>
              <Button onClick={() => setAdding((v) => !v)} size="sm" className="bg-[#002FA7] hover:bg-[#002685] text-white flex items-center gap-1.5" data-testid="corporate-add-toggle">
                <Plus className="w-4 h-4" /> Add staff
              </Button>
            </div>
            {adding && (
              <div className="p-4 border-b border-border bg-[#FAFAF7] grid grid-cols-1 md:grid-cols-5 gap-2 items-end" data-testid="corporate-add-form">
                <Field label="Name"><Input value={newUser.name || ""} onChange={(e) => setNewUser((u) => ({ ...u, name: e.target.value }))} className={inputCls} data-testid="corporate-new-name" /></Field>
                <Field label="Email"><Input type="email" value={newUser.email || ""} onChange={(e) => setNewUser((u) => ({ ...u, email: e.target.value }))} className={inputCls} data-testid="corporate-new-email" /></Field>
                <Field label="Phone (optional)"><Input value={newUser.phone || ""} onChange={(e) => setNewUser((u) => ({ ...u, phone: e.target.value }))} className={inputCls} data-testid="corporate-new-phone" /></Field>
                <Field label="Default cost centre">
                  <select value={newUser.default_cost_centre_id || ""} onChange={(e) => setNewUser((u) => ({ ...u, default_cost_centre_id: e.target.value }))} className={inputCls} data-testid="corporate-new-cc">
                    <option value="">None</option>
                    {costCentres.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </Field>
                <Button onClick={addUser} disabled={busy} className="bg-[#0A0A0A] text-white flex items-center justify-center gap-1.5" data-testid="corporate-new-submit">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Invite
                </Button>
              </div>
            )}
            {users === null ? (
              <div className="p-6 text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
            ) : users.length === 0 ? (
              <div className="p-10 text-center text-sm text-muted-foreground" data-testid="corporate-team-empty">No team members yet. Add your first staff member above.</div>
            ) : (
              <table className="w-full text-sm" data-testid="corporate-team-table">
                <thead><tr className="border-b border-border text-left label-mini text-muted-foreground">
                  <th className="px-4 py-2">Name</th><th className="px-4 py-2">Email</th><th className="px-4 py-2">Default cost centre</th><th className="px-4 py-2">Status</th><th className="px-4 py-2 text-right">Actions</th>
                </tr></thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-b border-border last:border-0" data-testid={`corporate-user-${u.id}`}>
                      <td className="px-4 py-2.5 font-semibold">{u.name}</td>
                      <td className="px-4 py-2.5 text-muted-foreground">{u.email}</td>
                      <td className="px-4 py-2.5">
                        <select
                          value={u.default_cost_centre_id || ""}
                          onChange={(e) => setStaffDefaultCC(u.id, e.target.value)}
                          className="border border-border px-2 py-1 text-xs bg-white focus:outline-none focus:border-[#002FA7]"
                          data-testid={`corporate-user-cc-${u.id}`}
                        >
                          <option value="">None</option>
                          {costCentres.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                          {/* keep an archived/removed default visible so it isn't silently lost */}
                          {u.default_cost_centre_id && !costCentres.some((c) => c.id === u.default_cost_centre_id) && (
                            <option value={u.default_cost_centre_id}>{u.default_cost_centre_name || "(archived)"}</option>
                          )}
                        </select>
                      </td>
                      <td className="px-4 py-2.5">
                        <span className={`label-mini px-1.5 py-0.5 border ${u.disabled ? "border-[#EF4444] text-[#991B1B] bg-[#FEF2F2]" : "border-[#10B981] text-[#065F46] bg-[#ECFDF5]"}`}>{u.disabled ? "DISABLED" : "ACTIVE"}</span>
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center justify-end gap-1">
                          <Button size="sm" variant="ghost" className="h-7 px-2 flex items-center gap-1" onClick={() => act(u.id, "send-reset")} data-testid={`corporate-reset-${u.id}`}><KeyRound className="w-3.5 h-3.5" /> Reset</Button>
                          {u.disabled
                            ? <Button size="sm" variant="ghost" className="h-7 px-2 flex items-center gap-1 text-[#065F46]" onClick={() => act(u.id, "enable")} data-testid={`corporate-enable-${u.id}`}><UserCheck className="w-3.5 h-3.5" /> Enable</Button>
                            : <Button size="sm" variant="ghost" className="h-7 px-2 flex items-center gap-1 text-[#991B1B]" onClick={() => act(u.id, "disable")} data-testid={`corporate-disable-${u.id}`}><UserX className="w-3.5 h-3.5" /> Disable</Button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {tab === "costcentres" && (
          <div className="bg-white border border-border" data-testid="corporate-costcentres">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <h2 className="font-heading font-black text-lg">Cost centres</h2>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer" data-testid="cc-show-archived">
                  <input type="checkbox" checked={showArchivedCC} onChange={(e) => setShowArchivedCC(e.target.checked)} /> Show archived
                </label>
                <Button onClick={() => setAddingCC((v) => !v)} size="sm" className="bg-[#002FA7] hover:bg-[#002685] text-white flex items-center gap-1.5" data-testid="cc-add-toggle">
                  <Plus className="w-4 h-4" /> New cost centre
                </Button>
              </div>
            </div>
            {addingCC && (
              <div className="p-4 border-b border-border bg-[#FAFAF7] grid grid-cols-1 md:grid-cols-4 gap-2 items-end" data-testid="cc-add-form">
                <Field label="Name"><Input value={newCC.name || ""} onChange={(e) => setNewCC((c) => ({ ...c, name: e.target.value }))} className={inputCls} data-testid="cc-new-name" /></Field>
                <Field label="Code (optional)"><Input value={newCC.code || ""} onChange={(e) => setNewCC((c) => ({ ...c, code: e.target.value }))} className={inputCls} data-testid="cc-new-code" /></Field>
                <Field label="Monthly budget £ (optional)"><Input type="number" min="0" step="0.01" value={newCC.monthly_budget || ""} onChange={(e) => setNewCC((c) => ({ ...c, monthly_budget: e.target.value }))} className={inputCls} data-testid="cc-new-budget" /></Field>
                <Button onClick={createCC} className="bg-[#0A0A0A] text-white flex items-center justify-center gap-1.5" data-testid="cc-new-submit"><Check className="w-4 h-4" /> Create</Button>
              </div>
            )}
            {manageCC === null ? (
              <div className="p-6 text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
            ) : manageCC.length === 0 ? (
              <div className="p-10 text-center text-sm text-muted-foreground" data-testid="cc-empty">No cost centres yet. Create one to start tagging spend.</div>
            ) : (
              <table className="w-full text-sm" data-testid="cc-table">
                <thead><tr className="border-b border-border text-left label-mini text-muted-foreground">
                  <th className="px-4 py-2">Name</th><th className="px-4 py-2">Code</th>
                  <th className="px-4 py-2">Monthly budget</th>
                  <th className="px-4 py-2">Used this month</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2 text-right">Actions</th>
                </tr></thead>
                <tbody>
                  {manageCC.map((c) => {
                    const b = ccBudgets[c.id];
                    const editing = Object.prototype.hasOwnProperty.call(editBudget, c.id);
                    return (
                      <tr key={c.id} className="border-b border-border last:border-0" data-testid={`cc-row-${c.id}`}>
                        <td className="px-4 py-2.5 font-semibold">{c.name}</td>
                        <td className="px-4 py-2.5 text-muted-foreground">{c.code || "—"}</td>
                        <td className="px-4 py-2.5">
                          {editing ? (
                            <div className="flex items-center gap-1">
                              <Input type="number" min="0" step="0.01" value={editBudget[c.id]} placeholder="0.00"
                                onChange={(e) => setEditBudget((m) => ({ ...m, [c.id]: e.target.value }))}
                                className="w-24 border border-border px-2 py-1 text-sm" data-testid={`cc-budget-input-${c.id}`} />
                              <Button size="sm" variant="ghost" className="h-7 px-1.5 text-[#065F46]" onClick={() => saveBudget(c.id)} data-testid={`cc-budget-save-${c.id}`}><Check className="w-3.5 h-3.5" /></Button>
                              <Button size="sm" variant="ghost" className="h-7 px-1.5 text-muted-foreground" onClick={() => setEditBudget((m) => { const n = { ...m }; delete n[c.id]; return n; })}><X className="w-3.5 h-3.5" /></Button>
                            </div>
                          ) : (
                            <button className="font-mono-tab hover:underline" onClick={() => setEditBudget((m) => ({ ...m, [c.id]: c.monthly_budget != null ? String(c.monthly_budget) : "" }))} data-testid={`cc-budget-edit-${c.id}`}>
                              {c.monthly_budget != null ? gbp(c.monthly_budget) : <span className="text-muted-foreground">Set budget</span>}
                            </button>
                          )}
                        </td>
                        <td className="px-4 py-2.5">
                          {b ? (
                            <div className="min-w-[120px]">
                              <div className="font-mono-tab">{gbp(b.spend)}{b.pct_used != null ? ` · ${b.pct_used}%` : ""}</div>
                              {b.monthly_budget != null && <BudgetBar status={b.status} pct={b.pct_used} />}
                            </div>
                          ) : <span className="text-muted-foreground">—</span>}
                        </td>
                        <td className="px-4 py-2.5"><BudgetBadge status={c.status === "archived" ? "archived" : (b ? b.status : (c.monthly_budget != null ? "under" : "none"))} testid={`cc-status-${c.id}`} /></td>
                        <td className="px-4 py-2.5">
                          <div className="flex items-center justify-end gap-1">
                            {c.status === "archived"
                              ? <Button size="sm" variant="ghost" className="h-7 px-2 flex items-center gap-1 text-[#065F46]" onClick={() => ccAction(c.id, "restore")} data-testid={`cc-restore-${c.id}`}><RotateCcw className="w-3.5 h-3.5" /> Restore</Button>
                              : <Button size="sm" variant="ghost" className="h-7 px-2 flex items-center gap-1 text-muted-foreground" onClick={() => ccAction(c.id, "archive")} data-testid={`cc-archive-${c.id}`}><Archive className="w-3.5 h-3.5" /> Archive</Button>}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}

        {tab === "bookings" && (
          <div className="bg-white border border-border" data-testid="corporate-bookings">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between">
              <h2 className="font-heading font-black text-lg">Company bookings</h2>
              <Button onClick={() => setBooking(true)} size="sm" className="bg-[#002FA7] hover:bg-[#002685] text-white flex items-center gap-1.5" data-testid="corporate-book-ride">
                <Car className="w-4 h-4" /> Book a ride
              </Button>
            </div>
            {bookings === null ? (
              <div className="p-6 text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
            ) : bookings.length === 0 ? (
              <div className="p-10 text-center text-sm text-muted-foreground" data-testid="corporate-bookings-empty">No bookings yet for your team.</div>
            ) : (
              <table className="w-full text-sm" data-testid="corporate-bookings-table">
                <thead><tr className="border-b border-border text-left label-mini text-muted-foreground">
                  <th className="px-4 py-2">Passenger</th><th className="px-4 py-2">Route</th><th className="px-4 py-2">Cost centre</th><th className="px-4 py-2">Status</th><th className="px-4 py-2 text-right">Fare</th>
                </tr></thead>
                <tbody>
                  {bookings.map((b) => (
                    <tr key={b.id} className="border-b border-border last:border-0" data-testid={`corporate-booking-${b.id}`}>
                      <td className="px-4 py-2.5 font-semibold">{b.passenger_name || "—"}</td>
                      <td className="px-4 py-2.5 text-muted-foreground text-xs">{b.pickup_address} → {b.dropoff_address}</td>
                      <td className="px-4 py-2.5 text-xs text-muted-foreground">{b.cost_centre_name || "—"}</td>
                      <td className="px-4 py-2.5"><span className="label-mini">{(b.status || "").replace(/_/g, " ")}</span></td>
                      <td className="px-4 py-2.5 text-right font-mono-tab">{b.fare != null ? gbp(b.fare) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {tab === "statements" && (
          <div className="space-y-4" data-testid="corporate-statements">
            <div className="flex flex-wrap items-end justify-between gap-3 bg-white border border-border p-4">
              <div>
                <label className="label-mini text-muted-foreground block mb-1">Statement month</label>
                <input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
                  className="border border-border px-3 py-2 text-sm focus:outline-none focus:border-[#002FA7]" data-testid="statement-month" />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => downloadStatement("csv")} className="flex items-center gap-1.5" data-testid="statement-download-csv"><Download className="w-4 h-4" /> CSV</Button>
                <Button size="sm" onClick={() => downloadStatement("pdf")} className="bg-[#0A0A0A] text-white flex items-center gap-1.5" data-testid="statement-download-pdf"><Download className="w-4 h-4" /> PDF</Button>
              </div>
            </div>

            {stmtLoading ? (
              <div className="p-6 text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading statement…</div>
            ) : statement ? (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="statement-summary">
                  <Stat label="Opening balance" value={gbp(statement.opening_balance)} testid="stmt-opening" />
                  <Stat label="Charges" value={gbp(statement.period_charges)} testid="stmt-charges" />
                  <Stat label="Payments" value={gbp(statement.period_payments)} testid="stmt-payments" />
                  <Stat label="Closing balance" value={gbp(statement.closing_balance)} testid="stmt-closing" />
                </div>

                <div className="bg-white border border-border" data-testid="statement-staff-spend">
                  <div className="px-4 py-3 border-b border-border flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-[#002FA7]" />
                    <h3 className="font-heading font-black">Spend by staff</h3>
                    <span className="label-mini text-muted-foreground ml-auto">{spend?.total_rides ?? 0} rides · {gbp(spend?.total_spend)}</span>
                  </div>
                  {(spend?.staff || []).length === 0 ? (
                    <div className="p-8 text-center text-sm text-muted-foreground" data-testid="statement-staff-empty">No account rides this month.</div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead><tr className="border-b border-border text-left label-mini text-muted-foreground">
                        <th className="px-4 py-2">Staff</th><th className="px-4 py-2 text-right">Rides</th><th className="px-4 py-2 text-right">Spend</th>
                      </tr></thead>
                      <tbody>
                        {spend.staff.map((s) => (
                          <tr key={s.passenger_id} className="border-b border-border last:border-0" data-testid={`stmt-staff-${s.passenger_id}`}>
                            <td className="px-4 py-2.5 font-semibold">{s.name}</td>
                            <td className="px-4 py-2.5 text-right font-mono-tab">{s.rides}</td>
                            <td className="px-4 py-2.5 text-right font-mono-tab">{gbp(s.spend)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                <div className="bg-white border border-border" data-testid="statement-cost-centre-spend">
                  <div className="px-4 py-3 border-b border-border flex items-center gap-2">
                    <Wallet className="w-4 h-4 text-[#002FA7]" />
                    <h3 className="font-heading font-black">Spend by cost centre</h3>
                  </div>
                  {(statement.by_cost_centre || []).length === 0 ? (
                    <div className="p-8 text-center text-sm text-muted-foreground" data-testid="statement-cc-empty">No account rides this month.</div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead><tr className="border-b border-border text-left label-mini text-muted-foreground">
                        <th className="px-4 py-2">Cost centre</th><th className="px-4 py-2 text-right">Rides</th>
                        <th className="px-4 py-2 text-right">Spend</th><th className="px-4 py-2 text-right">Budget</th>
                        <th className="px-4 py-2 text-right">Remaining</th><th className="px-4 py-2 text-right">% used</th>
                        <th className="px-4 py-2">Status</th>
                      </tr></thead>
                      <tbody>
                        {statement.by_cost_centre.map((c) => (
                          <tr key={c.cost_centre_id || "unassigned"} className="border-b border-border last:border-0" data-testid={`stmt-cc-${c.cost_centre_id || "unassigned"}`}>
                            <td className="px-4 py-2.5 font-semibold">{c.name}</td>
                            <td className="px-4 py-2.5 text-right font-mono-tab">{c.rides}</td>
                            <td className="px-4 py-2.5 text-right font-mono-tab">{gbp(c.spend)}</td>
                            <td className="px-4 py-2.5 text-right font-mono-tab">{c.monthly_budget != null ? gbp(c.monthly_budget) : "—"}</td>
                            <td className={`px-4 py-2.5 text-right font-mono-tab ${c.status === "over" ? "text-[#991B1B] font-semibold" : ""}`}>{c.remaining != null ? gbp(c.remaining) : "—"}</td>
                            <td className="px-4 py-2.5 text-right font-mono-tab">{c.pct_used != null ? `${c.pct_used}%` : "—"}</td>
                            <td className="px-4 py-2.5"><BudgetBadge status={c.status} testid={`stmt-cc-status-${c.cost_centre_id || "unassigned"}`} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                <div className="bg-white border border-border" data-testid="statement-line-items">
                  <div className="px-4 py-3 border-b border-border"><h3 className="font-heading font-black">Itemised rides ({statement.rides_count})</h3></div>
                  {statement.line_items.length === 0 ? (
                    <div className="p-8 text-center text-sm text-muted-foreground">No rides in this period.</div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead><tr className="border-b border-border text-left label-mini text-muted-foreground">
                        <th className="px-4 py-2">Date</th><th className="px-4 py-2">Staff</th><th className="px-4 py-2">Cost centre</th><th className="px-4 py-2">Journey</th><th className="px-4 py-2 text-right">Fare</th>
                      </tr></thead>
                      <tbody>
                        {statement.line_items.map((li) => (
                          <tr key={li.ride_id} className="border-b border-border last:border-0">
                            <td className="px-4 py-2.5 text-muted-foreground">{(li.date || "").slice(0, 10)}</td>
                            <td className="px-4 py-2.5 font-semibold">{li.customer}</td>
                            <td className="px-4 py-2.5 text-xs text-muted-foreground">{li.cost_centre || "Unassigned"}</td>
                            <td className="px-4 py-2.5 text-muted-foreground text-xs">{li.description}</td>
                            <td className="px-4 py-2.5 text-right font-mono-tab">{gbp(li.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            ) : (
              <div className="p-8 text-center text-sm text-muted-foreground">Select a month to view your statement.</div>
            )}
          </div>
        )}
      </div>

      {booking && (
        <CorporateBookingModal staff={users || []} costCentres={costCentres} onClose={() => setBooking(false)} onCreate={createBooking} />
      )}
    </div>
  );
}

const Stat = ({ label, value, testid }) => (
  <div className="bg-white border border-border p-4" data-testid={testid}>
    <div className="label-mini text-muted-foreground">{label}</div>
    <div className="font-heading text-xl font-black mt-0.5 capitalize">{value}</div>
  </div>
);

const Field = ({ label, children }) => (
  <label className="block">
    <Label className="label-mini text-muted-foreground mb-1 block">{label}</Label>
    {children}
  </label>
);

const BUDGET_BADGE = {
  under: { label: "Under budget", cls: "border-[#10B981] text-[#065F46] bg-[#ECFDF5]" },
  approaching: { label: "Approaching", cls: "border-[#F59E0B] text-[#92400E] bg-[#FEF3C7]" },
  over: { label: "Over budget", cls: "border-[#EF4444] text-[#991B1B] bg-[#FEE2E2]" },
  none: { label: "No budget", cls: "border-muted-foreground text-muted-foreground bg-secondary" },
  archived: { label: "Archived", cls: "border-muted-foreground text-muted-foreground bg-secondary" },
};

const BudgetBadge = ({ status, testid }) => {
  const cfg = BUDGET_BADGE[status] || BUDGET_BADGE.none;
  return <span className={`label-mini px-1.5 py-0.5 border ${cfg.cls}`} data-testid={testid}>{cfg.label.toUpperCase()}</span>;
};

const BUDGET_BAR = { under: "bg-[#10B981]", approaching: "bg-[#F59E0B]", over: "bg-[#EF4444]" };
const BudgetBar = ({ status, pct }) => (
  <div className="h-1.5 w-full bg-secondary mt-1 rounded-sm overflow-hidden">
    <div className={`h-full ${BUDGET_BAR[status] || "bg-[#10B981]"}`} style={{ width: `${Math.min(100, Math.max(2, pct || 0))}%` }} />
  </div>
);
